
import React, { useContext, useEffect, useState } from "react";
import { FiArrowLeft } from "react-icons/fi";
import { ApiContext } from "../../../context/MyContext";
import { BookingsIdentityHeader } from "./BookingsIdentityHeader";
import { BookingsTabs } from "./BookingsTabs";
import { BookingsWorkspace } from "./BookingsWorkspace";
import { CoachRosterTab } from "./CoachRosterTab";
import {
  COACH_MANAGER_DEPARTMENTS,
  describeCoachRole,
  getCoachDepartmentTitles,
} from "./coachRoster";
import { useCoachRoster } from "./useCoachRoster";

/**
 * The Bookings screen for anyone who publishes somebody else's availability —
 * a Fitness/Nutritionist Manager, or an executive.
 *
 * A manager is two things at once: they take calls themselves, *and* they
 * publish availability for the coaches assigned to them. So the roster is one
 * of two tabs, alongside the manager's own calendar. That calendar's shifts are
 * editable when the caller holds a `Manager` title, or is an executive — the
 * two branches of `may_act_for` that reach your own calendar;
 * `canEditOwnSlots` carries the answer down rather than re-deriving it here.
 *
 * An executive gets the same screen with `rosterScope="all"` — every coach in
 * the tenant rather than their own reporting line, because that is what
 * `may_act_for` grants them. They keep My calendar too, even with no
 * coach-department role: since `dcf5586` an Executive is a member's coach
 * whenever that member's subscription assigns them, and the subscription — not
 * a role — decides which department the call is in. One with no such role
 * lands on the table (that is still most of what they come here for), and
 * their own calendar's slot preview offers both coach departments, because the
 * frontend reads no subscription assignments to narrow it with.
 *
 * Opening a coach out of the table hands them the whole screen. Keeping the
 * caller's own header and tabs above it stacked two identical name-and-role
 * headers on one page, each labelling a different person's calendar — the
 * ambiguity is worse than the lost shortcut. "All coaches" brings the caller's
 * own screen back, still on the table.
 *
 * `useCoachRoster` is called **here**, not in `BookingsPage`. It used to be a
 * prop because the page had to read the reporting tree anyway, to detect a
 * caller standing above a coach manager; that screen is gone and the fork is
 * decided from context now, so the roster is a plain data dependency of the
 * one component that renders it.
 *
 * @param {Object} props
 * @param {"team"|"all"} [props.rosterScope="team"] - Whose coaches the table
 *   holds: the caller's direct reports, or every coach in the tenant.
 * @param {boolean} [props.canEditOwnSlots=false] - Whether /modify-coach-shift
 *   would accept an own-calendar write: the caller is an executive, or holds
 *   the `Manager` title.
 * @param {import('./bookingFocus').BookingFocus|null} [props.focus] - A booking
 *   to open on arrival; routed to the calendar of the coach it names.
 * @param {() => void} [props.onFocusHandled]
 */
export const ManagerBookings = ({
  rosterScope = "team",
  canEditOwnSlots = false,
  focus = null,
  onFocusHandled,
}) => {
  const { employeeData, departmentsWithRoles } = useContext(ApiContext);
  const allCoaches = rosterScope === "all";
  // An executive outside both coach departments: a coach only through
  // subscription assignments, whose department the frontend can't see.
  const subscriptionOnlyCoach =
    allCoaches && getCoachDepartmentTitles(employeeData, departmentsWithRoles).length === 0;

  const [tab, setTab] = useState(subscriptionOnlyCoach ? "my-team" : "my-calendar");
  const [selectedCoach, setSelectedCoach] = useState(null);

  const roster = useCoachRoster({ scope: rosterScope });

  // A booking to open on arrival (a chat card's "View booking"). It names the
  // coach whose calendar it is on; when that is somebody on the roster rather
  // than the caller, their calendar is opened first and the focus handed to it.
  const ownEmployeeId = employeeData?.data?.employee_id;
  const focusCoachId = focus?.coachEmployeeId;
  const focusIsOnAnotherCalendar =
    Boolean(focusCoachId) && String(focusCoachId) !== String(ownEmployeeId ?? "");
  useEffect(() => {
    if (!focusIsOnAnotherCalendar || roster.loading) return;
    const coach = roster.coaches.find(
      (row) => String(row.employeeId) === String(focusCoachId)
    );
    if (coach) {
      setSelectedCoach(coach);
    } else {
      // Not somebody this caller can open. Their own calendar will not have the
      // call either, and its workspace reports that.
      setTab("my-calendar");
    }
  }, [focusIsOnAnotherCalendar, focusCoachId, roster.loading, roster.coaches]);
  // Until the roster says whose calendar to open, the caller's own workspace
  // must not consume the focus and report "not on this calendar".
  const focusHere =
    focusIsOnAnotherCalendar && (roster.loading || selectedCoach) ? null : focus;

  const employee = employeeData?.data || {};
  const managerName = `${employee.first_name || ""} ${employee.last_name || ""}`.trim();
  // `describeCoachRole` covers every caller this screen renders for, including
  // the `rosterOnly` executive it once answered null for — it names that
  // standing "Executive" now, so the fallback sentence that used to live here
  // would be a second module deciding how to describe the same person.
  const roleLine = describeCoachRole(employeeData, departmentsWithRoles);

  const rosterPanel = (
    <CoachRosterTab
      coaches={roster.coaches}
      loading={roster.loading}
      error={roster.error}
      refresh={roster.refresh}
      hasEmployeeProfile={roster.hasEmployeeProfile}
      onSelectCoach={setSelectedCoach}
      // The Manager column says which team a coach is on, which is only worth
      // a column when the table spans more than one. On a manager's own team
      // every row would read "You".
      showManager={allCoaches}
      {...(allCoaches
        ? {
          // Every coach in the tenant, so the departments are the switcher —
          // and on this caller's screen there is no tab strip above to give
          // the table its line of orientation.
          departmentTabs: true,
          caption:
            "Every Fitness and Nutritionist coach. Click a row to open their calendar.",
          emptyTitle: "No coaches to show",
          emptyHint:
            "This lists every Fitness and Nutritionist coach in the company. An empty list means nobody currently holds one of those roles.",
        }
        : null)}
    />
  );

  // An open coach replaces the manager's own screen rather than nesting inside
  // it. `tab` is left untouched, so backing out lands on My team where they
  // left off.
  //
  // Deliberately unwrapped: the width cap belongs to the panel, not the screen.
  // Capping here made a coach's Bookings dashboard narrower than the caller's
  // own, which is the same component showing the same shape of data.
  if (selectedCoach) {
    return (
      <CoachWorkspace
        coach={selectedCoach}
        onBack={() => setSelectedCoach(null)}
        focus={
          String(selectedCoach.employeeId) === String(focusCoachId ?? "") ? focus : null
        }
        onFocusHandled={onFocusHandled}
      />
    );
  }

  const identityHeader = managerName ? (
    <BookingsIdentityHeader
      name={managerName}
      subtitle={roleLine}
      colorSeed={employee.employee_id}
    />
  ) : null;

  // Two, not three: the manager's own bookings and their own slots are now one
  // workspace under one calendar, so what used to be two sibling tabs here are
  // sub-tabs inside the first.
  const tabs = [
    { id: "my-calendar", label: "My calendar" },
    {
      id: "my-team",
      // "My team" only when it *is* their team. An executive's table is every
      // coach in the company, most of whom report to somebody else.
      label: allCoaches ? "Coaches" : "My team",
      // Withheld until the roster has actually loaded — a 0 beside the label
      // while the fetch is in flight reads as "you manage nobody".
      count: roster.loading || roster.error ? null : roster.coaches.length,
    },
  ];

  return (
    <>
      <header className="mb-5">
        {identityHeader}

        <div className="mt-4">
          <BookingsTabs
            value={tab}
            onChange={setTab}
            items={tabs}
            label="Bookings views"
          />
        </div>
      </header>

      <div
        role="tabpanel"
        id={`bk-tab-${tab}-panel`}
        aria-labelledby={`bk-tab-${tab}`}
        className={tab === "my-team" ? "max-w-[1120px]" : undefined}
      >
        {tab === "my-team" ? (
          rosterPanel
        ) : (
          <BookingsWorkspace
            shiftsReadOnly={!canEditOwnSlots}
            tabLabels={{ shifts: "My shifts" }}
            // Distinct from the tab strip above it, or the two tablists would
            // generate the same ids and each tab would point at the other's
            // panel.
            idPrefix="bk-own"
            departmentChoices={subscriptionOnlyCoach ? COACH_MANAGER_DEPARTMENTS : undefined}
            focus={focusHere}
            onFocusHandled={onFocusHandled}
          />
        )}
      </div>
    </>
  );
};

/**
 * One coach, opened from the table: their bookings and their slots.
 *
 * Owns the page's h1 while it is up, because it *is* the page while it is up —
 * the caller's own header is not rendered behind it: back link, avatar and name
 * across the full main area, then the same workspace the caller gets on their
 * own screen with somebody else's id in it.
 *
 * No tab strip of its own any more. `BookingsWorkspace` already has exactly
 * the two sub-tabs this screen used to render above it, and a strip of two
 * over a strip of two was two controls for one choice.
 *
 * Every row on the table is one the caller may act on — the reporting tree
 * returns nobody else — so shift editing is fully live here. There is no
 * read-only variant of this screen, and no row that opens onto an explanation
 * of why it does nothing.
 *
 * @param {Object} props
 * @param {Object} props.coach - The roster row that was opened.
 * @param {() => void} props.onBack - Return to the roster.
 * @param {Object|null} [props.focus] - A booking on this coach's calendar to open.
 * @param {() => void} [props.onFocusHandled]
 */
const CoachWorkspace = ({ coach, onBack, focus = null, onFocusHandled }) => (
  <>
    <div className="mb-5">
      <button
        type="button"
        onClick={onBack}
        className="inline-flex min-h-[44px] items-center gap-2 rounded-lg px-3 text-sm text-bc-text-2 transition-colors hover:bg-light-gray/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-primary focus-visible:ring-offset-1"
      >
        <FiArrowLeft aria-hidden="true" />
        All coaches
      </button>
      {/*
        The same header the manager gets on their own screen, deliberately: both
        are the same shape — someone's name, their designation, then their
        calendars — so the only thing that changes when you open a coach is
        whose name is on it.
      */}
      <div className="mt-2">
        <BookingsIdentityHeader
          name={coach.name}
          subtitle={[coach.roleTitle, coach.departmentTitle].filter(Boolean).join(" · ")}
          colorSeed={coach.employeeId}
        />
      </div>
    </div>

    {/*
      Shifts are always editable here. `may_act_for` grants somebody else's
      calendar on the live reporting line alone, with no title asked of either
      side (or on executive standing) — which is the same rule that put this
      row on the table, so a row that opens is a row whose shifts can be set.
      `assigned_by_id` is derived server-side from the employee row the caller
      acted through, so the write needs nothing here beyond naming the coach.

      The narrow case this still leaves is an edge through a deactivated role
      or department, which `may_act_for` refuses and the hierarchy read does
      not exclude. It fails legibly: the API's 403 explains which link is
      missing or expired, and `wrapCommsError` keeps that message so it reaches
      the toast verbatim.

      Bookings are writable here too, and by the same reasoning: both
      `fetch-booking-records` and `validate_booking_ownership` now resolve from
      `actionable_employee_ids` (owned *and* managed) plus an executive bypass,
      so the reporting line that put this row on the table is the line that
      grants the correction. What a manager may *set* is narrower than what they
      may reach — `validate_booking_authority` gives them the corrective set
      (cancel, no show, completed) and not the power to book or confirm on a
      member's behalf — which is `bookingAuthority.js`'s job to mirror.

      The read/write asymmetry this comment used to describe is gone. It said a
      manager "can see a member's booking reason and cannot cancel or
      reschedule it"; that stopped being true when the write path was rebound to
      the same id set, and reschedule remains unbuilt for a different reason —
      neither a coach nor a manager may set that status at all.
    */}
    <BookingsWorkspace
      employeeId={coach.employeeId}
      coachName={coach.name}
      departmentTitle={coach.departmentTitle}
      shiftsReadOnly={false}
      idPrefix="bk-coach-tab"
      focus={focus}
      onFocusHandled={onFocusHandled}
    />
  </>
);
